import React, { useState, ChangeEvent, KeyboardEvent, MouseEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { Edit, Trash2 } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import CustomerForm from "../components/CustomerForm";
import SearchInput from "../components/SearchInput";
import "./CustomersList.css";

interface Customer {
  id: number;
  name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  industry?: string;
  company_size?: string;
  employee_count?: number;
  founded_year?: number;
  owner_name?: string;
  website?: string;
  status?: string;
  description?: string;
  notes?: string;
}

interface CustomersResponse {
  data?: Customer[];
}

interface CustomerFilters {
  status: string;
}

const CustomersList: React.FC = () => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [filters, setFilters] = useState<CustomerFilters>({
    status: "",
  });
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  // Handle debounced search - triggered by SearchInput component
  const handleDebouncedSearch = (value: string) => {
    setDebouncedSearch(value);
  };

  // Fetch customers
  const {
    data: customersData,
    isLoading,
    error,
  } = useQuery<{ customers: Customer[]; total: number }>(
    ["customers", debouncedSearch, filters],
    async () => {
      const params: Record<string, string> = {
        q: debouncedSearch,
        ...filters,
      };
      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });
      const response = (await recruitingAPI.getCustomers(
        params,
      )) as CustomersResponse;
      return {
        customers: response.data || [],
        total: response.data?.length || 0,
      };
    },
    { keepPreviousData: true },
  );

  const customers = customersData?.customers || [];

  // Delete mutation
  const deleteMutation = useMutation(
    async (id: number) => await recruitingAPI.deleteCustomer(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("customers");
      },
    },
  );

  const handleFilterChange = (
    filterName: keyof CustomerFilters,
    value: string,
  ): void => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const handleCreate = (): void => {
    setEditingCustomer(null);
    setShowCreateModal(true);
  };

  const handleEdit = (customer: Customer): void => {
    setEditingCustomer(customer);
    setShowCreateModal(true);
  };

  const handleDelete = async (
    customerId: number,
    customerName: string,
  ): Promise<void> => {
    if (window.confirm(t("customers.deleteConfirm", { name: customerName }))) {
      try {
        await deleteMutation.mutateAsync(customerId);
      } catch (err) {
        console.error("Error deleting customer:", err);
        alert(t("customers.deleteFailed"));
      }
    }
  };

  const handleSave = (): void => {
    setShowCreateModal(false);
    setEditingCustomer(null);
    queryClient.invalidateQueries("customers");
  };

  const handleCancel = (): void => {
    setShowCreateModal(false);
    setEditingCustomer(null);
  };

  return (
    <div className="customers-list">
      <div className="customers-header">
        <div className="header-content">
          <h1>{t("customers.list.title")}</h1>
          <button className="btn-primary" onClick={handleCreate}>
            {t("customers.newButton")}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="search-filters">
        <div className="search-section">
          <div className="search-input-group">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onDebouncedChange={handleDebouncedSearch}
              placeholder={t("customers.searchPlaceholder")}
              ariaLabel={t("customers.searchAriaLabel")}
              showSearchIcon
              showClearButton
            />
          </div>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <select
              className="filter-select"
              value={filters.status}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                handleFilterChange("status", e.target.value)
              }
            >
              <option value="">{t("customers.filters.allStatus")}</option>
              <option value="active">{t("common:status.active")}</option>
              <option value="inactive">{t("common:status.inactive")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customers Grid */}
      {(() => {
        if (isLoading) {
          return (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t("customers.loading")}</p>
            </div>
          );
        }
        if (error) {
          return (
            <div className="empty-state">
              <h3>{t("customers.errorLoading")}</h3>
              <p>
                {(error as Error).message || t("customers.errorLoadingGeneric")}
              </p>
            </div>
          );
        }
        if (customers.length === 0) {
          return (
            <div className="empty-state">
              <h3>{t("customers.emptyStates.noCustomers")}</h3>
              <p>
                {debouncedSearch || Object.values(filters).some(Boolean)
                  ? t("customers.emptyStates.tryAdjusting")
                  : t("customers.emptyStates.getStarted")}
              </p>
            </div>
          );
        }
        return (
          <div className="customers-grid">
            {customers.map((customer) => (
              <div key={customer.id} className="customer-card">
                <div className="customer-header">
                  <div className="customer-name-section">
                    <h3>{customer.company_name || customer.name}</h3>
                    {customer.company_name && customer.name && (
                      <p className="customer-contact">{customer.name}</p>
                    )}
                  </div>
                  <div className="customer-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(customer)}
                      title={t("common:actions.edit")}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() =>
                        handleDelete(
                          customer.id,
                          customer.company_name || customer.name || "",
                        )
                      }
                      title={t("common:actions.delete")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="customer-details">
                  {customer.email && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.email")}
                      </span>
                      <span className="value">{customer.email}</span>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.phone")}
                      </span>
                      <span className="value">{customer.phone}</span>
                    </div>
                  )}
                  {customer.industry && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.industry")}
                      </span>
                      <span className="value">{customer.industry}</span>
                    </div>
                  )}
                  {customer.company_size && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.size")}
                      </span>
                      <span className="value">
                        {customer.company_size.charAt(0).toUpperCase() +
                          customer.company_size.slice(1)}
                      </span>
                    </div>
                  )}
                  {customer.employee_count && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.employees")}
                      </span>
                      <span className="value">
                        {customer.employee_count.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {customer.founded_year && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.founded")}
                      </span>
                      <span className="value">{customer.founded_year}</span>
                    </div>
                  )}
                  {customer.owner_name && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.owner")}
                      </span>
                      <span className="value">{customer.owner_name}</span>
                    </div>
                  )}
                  {customer.website && (
                    <div className="detail-row">
                      <span className="label">
                        {t("customers.labels.website")}
                      </span>
                      <span className="value">
                        <a
                          href={customer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {customer.website}
                        </a>
                      </span>
                    </div>
                  )}
                </div>

                <div className="customer-badges">
                  {customer.status && (
                    <span
                      className={`badge status-badge status-${customer.status}`}
                    >
                      {customer.status}
                    </span>
                  )}
                  {customer.company_size && (
                    <span className="badge size-badge">
                      {customer.company_size}
                    </span>
                  )}
                </div>

                {customer.description && (
                  <div className="customer-description">
                    <p>
                      {customer.description.substring(0, 150)}
                      {customer.description.length > 150 ? "..." : ""}
                    </p>
                  </div>
                )}

                {customer.notes && (
                  <div className="customer-notes">
                    <p>
                      {customer.notes.substring(0, 100)}
                      {customer.notes.length > 100 ? "..." : ""}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={handleCancel}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Escape") handleCancel();
          }}
          role="none"
          tabIndex={-1}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
          <dialog
            className="modal large-modal"
            onClick={(e: MouseEvent<HTMLDialogElement>) => e.stopPropagation()}
            aria-modal="true"
            aria-label="Customer form"
            open
          >
            <CustomerForm
              customer={editingCustomer}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={async (customerId: number) => {
                await handleDelete(
                  customerId,
                  editingCustomer
                    ? editingCustomer.company_name || editingCustomer.name || ""
                    : "",
                );
                handleCancel();
              }}
            />
          </dialog>
        </div>
      )}
    </div>
  );
};

export default CustomersList;
